/**
 * The stranded-state check (#1652; spec 6.7, "Enrolment is only ever additive").
 *
 * `courses-devices/stranded` is the shape the approval screen exists never to
 * see: this phone holds no key, the API says enrolled, no APPROVED device
 * other than this install, no recovery code. Under decision 40 it cannot happen — the key is durable on a
 * device before the server commits, a sent attempt is reconciled before
 * anything is discarded, and the only route to the approval screen is a
 * status read that returned a DIFFERENT bundle, which means a real approver
 * exists. So the shape is either a member who has genuinely lost every phone
 * and never saved a code, or a bug somewhere above, and the two are told
 * apart by what this phone still holds: an attempt row, a provisional copy.
 *
 * A launch watcher, like `reset-notice.js` and `enrolment-resume.js`, rather
 * than something the approval page does: the shape has to be seen wherever
 * the member is — on a locked Hub, on the Devices screen, or never tapping
 * through to the approval screen at all. What Try again does about it is
 * `enrolment.js`'s `retryRecovery`; this file only reports.
 */
import { loadKeyTools } from './enrolment';
import { peekProvisionalCopy } from './enrolment-attempt';

/** One string for the RUM error, so every occurrence groups under one facet. */
export const STRANDED_ERROR_MESSAGE = 'courses-e2ee: device stranded on the approval screen';

const DEFAULT_DEPS = Object.freeze({
  loadKeyTools,
  peekProvisionalCopy,
  /** Lazy: `datadog.js` pulls in both SDKs and Mixpanel; `ota.js` loads it the same way. */
  reportError: (error, context) =>
    import('@/utils/datadog').then(({ addDatadogRumError }) => addDatadogRumError(error, context)),
});

function currentMember(store) {
  const memberId = store.getters['courses-keys/memberId'];
  return Number.isInteger(memberId) && memberId > 0 ? memberId : 0;
}

/**
 * The loud diagnostic. What it carries is what tells a bug from a genuine
 * loss: whether an attempt row survives on this phone, and whether the
 * provisional slot still holds a copy. `console.error` reaches Datadog Logs
 * through `forwardErrorsToLogs`; the RUM error is what a monitor watches.
 *
 * @param {{ store: object, deps?: object }} args
 * @returns {Promise<object|null>} the context reported, or null with no member
 */
export async function reportStrandedState({ store, deps: overrides } = {}) {
  if (!store) throw new TypeError('reportStrandedState needs the root Vuex store');
  const deps = overrides ? { ...DEFAULT_DEPS, ...overrides } : DEFAULT_DEPS;
  const memberId = currentMember(store);
  if (!memberId) return null;

  let provisionalCopy = null;
  try {
    const tools = await deps.loadKeyTools();
    provisionalCopy = await deps.peekProvisionalCopy({ tools, memberId });
  } catch (error) {
    console.warn(
      '[CoursesE2EE] The platform could not be asked for the stranded diagnostic:',
      error,
    );
  }
  const context = {
    memberId,
    attemptState: store.getters['courses-keys/attemptState'],
    provisionalCopy,
    thisDeviceStatus: store.getters['courses-devices/thisDeviceStatus'],
    approvedDevices: store.getters['courses-devices/approvedDevices'].length,
  };
  console.error(
    `[CoursesE2EE] ${STRANDED_ERROR_MESSAGE}: the API says enrolled, no other device is approved and no recovery code is saved.`,
    context,
  );
  await deps.reportError(new Error(STRANDED_ERROR_MESSAGE), context);
  return context;
}

/**
 * Report the stranded state once per member per session, whenever the store
 * says so. Installed once by App.vue beside the other launch checks. Never
 * throws and never blocks.
 *
 * @param {{ store: object, report?: typeof reportStrandedState }} args
 * @returns {() => void} stops watching
 */
export function installStrandedStateCheck({ store, report = reportStrandedState } = {}) {
  if (!store) throw new TypeError('installStrandedStateCheck needs the root Vuex store');

  // One derived value rather than a tuple: the member id while the shape
  // holds, 0 otherwise, so the watcher fires on the shape and on the member.
  const signal = () =>
    store.getters['courses-devices/stranded'] === true ? currentMember(store) : 0;

  const reported = new Set();
  const check = member => {
    if (!member || reported.has(member)) return;
    reported.add(member);
    report({ store }).catch(error => {
      console.warn('[CoursesE2EE] The stranded state was not reported:', error);
    });
  };

  check(signal());
  return store.watch(signal, check);
}
